// =============================================
// 1. UPDATED CLIENT REPORT WITH PIVOT FUNCTIONALITY
// components/reports/ClientReport.js
// =============================================

import React, { useMemo, useState } from 'react'
import { Building2, TrendingUp, Clock, DollarSign, UserCheck, Users, ChevronDown, ChevronRight } from 'lucide-react'
import { processTimeEntries, calculateEntryCounts } from '../../utils/timeSplitUtils'

// Move formatRoleName outside component to fix initialization error
const formatRoleName = (role) => {
  if (!role || role === 'unassigned') return 'Unassigned'
  
  const roleLabels = {
    'senior_developer': 'Senior Developer',
    'project_manager': 'Project Manager', 
    'developer': 'Developer',
    'designer': 'Designer',
    'qa_engineer': 'QA Engineer',
    'tech_lead': 'Tech Lead',
    'consultant': 'Consultant',
    'architect': 'Architect',
    'junior_developer': 'Junior Developer',
    'devops_engineer': 'DevOps Engineer',
    'business_analyst': 'Business Analyst',
    'product_manager': 'Product Manager'
  }
  
  return roleLabels[role] || role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
}

export const ClientReport = ({ data }) => {
  const [pivotBy, setPivotBy] = useState('role') // 'role' or 'user'
  const [expandedClients, setExpandedClients] = useState(new Set())

  const clientData = useMemo(() => {
    // Process time entries to handle splits
    const expandedEntries = processTimeEntries(data)
    const clientMap = new Map()
    
    // Keep track of original entries per client
    const clientOriginalCounts = new Map()
    data.forEach(entry => {
      const key = entry.client_id
      clientOriginalCounts.set(key, (clientOriginalCounts.get(key) || 0) + 1)
    })
    
    expandedEntries.forEach(entry => {
      const key = entry.client_id
      if (!clientMap.has(key)) {
        clientMap.set(key, {
          id: key,
          name: entry.client_name,
          totalHours: 0,
          billableHours: 0,
          revenue: 0,
          expandedEntries: 0,
          splitCount: 0,
          projects: new Set(),
          users: new Set(),
          roles: new Map(), // For pivot by role
          userDetails: new Map() // For pivot by user
        })
      }
      
      const client = clientMap.get(key)
      client.totalHours += entry.duration_hours || 0
      client.expandedEntries += 1
      if (entry.is_split_entry) client.splitCount += 1
      client.projects.add(entry.project_id)
      client.users.add(entry.user_id)
      
      // Track by role for pivot
      const role = entry.bill_rate_role || 'unassigned'
      if (!client.roles.has(role)) {
        client.roles.set(role, {
          role: role,
          roleName: formatRoleName(role),
          totalHours: 0,
          billableHours: 0,
          revenue: 0,
          entries: 0,
          users: new Set()
        })
      }
      const roleData = client.roles.get(role)
      roleData.totalHours += entry.duration_hours || 0
      roleData.entries += 1
      roleData.users.add(entry.user_id)
      if (entry.is_billable) {
        roleData.billableHours += entry.duration_hours || 0
        roleData.revenue += (entry.duration_hours || 0) * (entry.hourly_rate || 0)
      }
      
      // Track by user for pivot
      const userId = entry.user_id
      if (!client.userDetails.has(userId)) {
        client.userDetails.set(userId, {
          userId: userId,
          userName: entry.user_name,
          role: entry.bill_rate_role || 'unassigned',
          totalHours: 0,
          billableHours: 0,
          revenue: 0,
          entries: 0
        })
      }
      const userData = client.userDetails.get(userId)
      userData.totalHours += entry.duration_hours || 0
      userData.entries += 1
      if (entry.is_billable) {
        userData.billableHours += entry.duration_hours || 0
        userData.revenue += (entry.duration_hours || 0) * (entry.hourly_rate || 0)
      }
      
      if (entry.is_billable) {
        client.billableHours += entry.duration_hours || 0
        client.revenue += (entry.duration_hours || 0) * (entry.hourly_rate || 0)
      }
    })
    
    return Array.from(clientMap.values()).map(client => ({
      ...client,
      projectCount: client.projects.size,
      userCount: client.users.size,
      utilizationRate: client.totalHours > 0 ? (client.billableHours / client.totalHours) * 100 : 0,
      avgRate: client.billableHours > 0 ? client.revenue / client.billableHours : 0,
      originalEntries: clientOriginalCounts.get(client.id) || 0,
      rolesList: Array.from(client.roles.values()).map(role => ({
        ...role,
        userCount: role.users.size,
        utilizationRate: role.totalHours > 0 ? (role.billableHours / role.totalHours) * 100 : 0,
        avgRate: role.billableHours > 0 ? role.revenue / role.billableHours : 0
      })).sort((a, b) => b.revenue - a.revenue),
      usersList: Array.from(client.userDetails.values()).map(user => ({
        ...user,
        roleName: formatRoleName(user.role),
        utilizationRate: user.totalHours > 0 ? (user.billableHours / user.totalHours) * 100 : 0,
        avgRate: user.billableHours > 0 ? user.revenue / user.billableHours : 0
      })).sort((a, b) => b.revenue - a.revenue)
    })).sort((a, b) => b.revenue - a.revenue)
  }, [data])

  const toggleClientExpanded = (clientId) => {
    const newExpanded = new Set(expandedClients)
    if (newExpanded.has(clientId)) {
      newExpanded.delete(clientId)
    } else {
      newExpanded.add(clientId)
    }
    setExpandedClients(newExpanded)
  }

  if (!clientData.length) {
    return (
      <div className="text-center py-12">
        <Building2 className="w-12 h-12 text-slate-400 mx-auto mb-4" />
        <div className="text-slate-600">No client data found for the selected filters</div>
      </div>
    )
  }

  const totalSplitEntries = clientData.reduce((sum, client) => sum + client.splitCount, 0)

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600">Total Clients</p>
              <p className="text-2xl font-bold text-blue-900">{clientData.length}</p>
            </div>
            <Building2 className="w-8 h-8 text-blue-600" />
          </div>
        </div>
        
        <div className="bg-green-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600">Total Cost</p>
              <p className="text-2xl font-bold text-green-900">
                ${clientData.reduce((sum, client) => sum + client.revenue, 0).toFixed(0)}
              </p>
            </div>
            <DollarSign className="w-8 h-8 text-green-600" />
          </div>
        </div>
        
        <div className="bg-purple-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-purple-600">Total Hours</p>
              <p className="text-2xl font-bold text-purple-900">
                {clientData.reduce((sum, client) => sum + client.totalHours, 0).toFixed(1)}
              </p>
            </div>
            <Clock className="w-8 h-8 text-purple-600" />
          </div>
        </div>
        
        <div className="bg-orange-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-orange-600">Split Entries</p>
              <p className="text-2xl font-bold text-orange-900">
                {totalSplitEntries}
              </p>
            </div>
            <TrendingUp className="w-8 h-8 text-orange-600" />
          </div>
        </div>
      </div>

      {/* Pivot Controls */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-lg font-semibold text-slate-900">Client Performance Analysis</h4>
            <p className="text-sm text-slate-600 mt-1">Cost and hour breakdown by client with pivot analysis</p>
          </div>
          
          <div className="flex items-center space-x-4">
            <span className="text-sm text-slate-700">Pivot by:</span>
            <div className="flex bg-slate-100 rounded-lg p-1">
              <button
                onClick={() => setPivotBy('role')}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  pivotBy === 'role'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <UserCheck className="w-4 h-4 inline mr-1" />
                Role
              </button>
              <button
                onClick={() => setPivotBy('user')}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  pivotBy === 'user'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Users className="w-4 h-4 inline mr-1" />
                User
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Hierarchical Client Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Client / {pivotBy === 'role' ? 'Role' : 'Team Member'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  {pivotBy === 'role' ? 'Team Members' : 'Role'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Projects
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Total Hours
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Billable Hours
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Cost
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Utilization
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Avg Rate
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {clientData.map((client, clientIndex) => {
                const isExpanded = expandedClients.has(client.id)
                const pivotData = pivotBy === 'role' ? client.rolesList : client.usersList
                
                return (
                  <React.Fragment key={client.id}>
                    {/* Client Summary Row */}
                    <tr 
                      className={`${clientIndex % 2 === 0 ? 'bg-white' : 'bg-slate-50'} cursor-pointer hover:bg-blue-50`}
                      onClick={() => toggleClientExpanded(client.id)}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-slate-400 mr-2" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-slate-400 mr-2" />
                          )}
                          <div>
                            <div className="font-medium text-slate-900">{client.name}</div>
                            <div className="text-sm text-slate-500">
                              {client.originalEntries} entries{client.splitCount > 0 ? ` (+${client.splitCount} splits)` : ''}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {pivotBy === 'role' ? client.userCount : formatRoleName(client.usersList[0]?.role)}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {client.projectCount}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-slate-900">
                        {client.totalHours.toFixed(1)}h
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-blue-600">
                        {client.billableHours.toFixed(1)}h
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-green-600">
                        ${client.revenue.toFixed(0)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="w-16 bg-slate-200 rounded-full h-2 mr-2">
                            <div 
                              className={`h-2 rounded-full ${
                                client.utilizationRate >= 80 ? 'bg-green-500' :
                                client.utilizationRate >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${Math.min(client.utilizationRate, 100)}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-slate-900">
                            {client.utilizationRate.toFixed(1)}%
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-slate-900">
                        ${client.avgRate.toFixed(0)}/h
                      </td>
                    </tr>

                    {/* Pivot Sub-rows */}
                    {isExpanded && pivotData.map((item, itemIndex) => (
                      <tr key={`${client.id}-${pivotBy}-${itemIndex}`} className="bg-slate-25 border-l-4 border-l-blue-200">
                        <td className="px-6 py-3 pl-12">
                          <div className="flex items-center text-sm">
                            {pivotBy === 'role' ? (
                              <>
                                <UserCheck className="w-4 h-4 text-slate-400 mr-2" />
                                <span className="font-medium text-slate-700">{item.roleName}</span>
                              </>
                            ) : (
                              <>
                                <Users className="w-4 h-4 text-slate-400 mr-2" />
                                <span className="font-medium text-slate-700">{item.userName}</span>
                              </>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-3 text-sm text-slate-600">
                          {pivotBy === 'role' ? item.userCount : item.roleName}
                        </td>
                        <td className="px-6 py-3 text-sm text-slate-600">-</td>
                        <td className="px-6 py-3 text-sm text-slate-700">
                          {item.totalHours.toFixed(1)}h
                        </td>
                        <td className="px-6 py-3 text-sm text-blue-600">
                          {item.billableHours.toFixed(1)}h
                        </td>
                        <td className="px-6 py-3 text-sm font-medium text-green-600">
                          ${item.revenue.toFixed(0)}
                        </td>
                        <td className="px-6 py-3">
                          <div className="flex items-center">
                            <div className="w-12 bg-slate-200 rounded-full h-1.5 mr-2">
                              <div 
                                className={`h-1.5 rounded-full ${
                                  item.utilizationRate >= 80 ? 'bg-green-500' :
                                  item.utilizationRate >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                                }`}
                                style={{ width: `${Math.min(item.utilizationRate, 100)}%` }}
                              />
                            </div>
                            <span className="text-xs text-slate-700">
                              {item.utilizationRate.toFixed(1)}%
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-3 text-sm text-slate-700">
                          ${item.avgRate.toFixed(0)}/h
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}


export default ClientReport